/**
 * ONE MEMBER — points, gift cards and the permanent history.
 *
 * Everything the front desk does for one person is a big button here, and
 * every one of those actions appears underneath in History, newest first.
 * There is NO way to edit or remove a history entry: a mistake is put right
 * with a correction, which is a NEW entry pointing at the old one.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { ACTION_STYLE, ActionIcon } from './actionStyles';
import { downloadConsentCsv } from './consentCsv';
import { money } from '../../lib/money';

const ACTION_LABELS = {
    registered: 'Registered',
    confirmed: 'Confirmed their account',
    consent_given: 'Consent given',
    pin_reset_sent: 'PIN reset email sent',
    pin_reset_done: 'New PIN chosen',
    points_added: 'Points added',
    points_removed: 'Points removed',
    visit_points: 'Visit points',
    opening_gift: 'Opening gift points',
    referral_points: 'Referral points',
    cash_in: 'Cashed in points',
    gift_card_loaded: 'Gift card loaded',
    gift_card_created: 'Gift card created',
    gift_card_given: 'Gift card given',
    gift_card_redeemed: 'Gift card used',
    gift_card_reward: 'Gift card earned',
    gift_card_deleted: 'Gift card removed',
    gift_card_restored: 'Gift card restored',
    member_deleted: 'Member removed',
    member_restored: 'Member restored',
    member_deletion_requested: 'Member asked to be removed',
};

const when = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const Change = ({ entry }) => {
    const b = entry.before || {};
    const a = entry.after || {};
    if (b.points !== undefined || a.points !== undefined) {
        return <span>{(b.points ?? 0).toLocaleString()} → {(a.points ?? 0).toLocaleString()}</span>;
    }
    if (a.code) return <span>{a.code}{a.value ? ` · ${money(a.value)}` : ''}</span>;
    if (a.status) return <span>{b.status || '—'} → {a.status}</span>;
    return <span>—</span>;
};

export const MemberScreen = ({ memberId, onClose, onChanged,
                               startTab = 'actions' }) => {
    const { t } = useTranslation();
    const [detail, setDetail] = useState(null);
    const [entries, setEntries] = useState([]);
    const [filter, setFilter] = useState('');
    const [form, setForm] = useState('');          // which action form is open
    const [amount, setAmount] = useState('');
    const [count, setCount] = useState('2');
    const [note, setNote] = useState('');
    const [reverses, setReverses] = useState('');
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState('');
    const [error, setError] = useState('');
    const [tab, setTab] = useState(startTab);
    const [name, setName] = useState('');

    const load = useCallback(async () => {
        try {
            const d = await api.get(`/loyalty/members/${memberId}`);
            setDetail(d.data);
            setName(d.data?.member?.first_name || '');
            const h = await api.get(`/loyalty/members/${memberId}/history`,
                { params: { action: filter || undefined } });
            setEntries(h.data?.entries || []);
        } catch (e) {
            setError(e.response?.data?.detail || t('common.error', 'Something went wrong'));
        }
    }, [memberId, filter, t]);

    useEffect(() => { load(); }, [load]);

    const fail = (e) => {
        let d = e.response?.data?.detail;
        if (typeof d === 'string' && d.trim().startsWith('{')) {
            try { d = JSON.parse(d); } catch { /* text */ }
        }
        if (d?.reason === 'below_zero') {
            return t('loyalty.cc.belowZero',
                'That would go below zero — this member has {{balance}} points',
                { balance: d.balance });
        }
        if (d?.reason === 'too_soon') {
            return t('loyalty.cc.visitTooSoon', 'A visit was already credited — try again in {{hours}} h',
                { hours: d.hours_left });
        }
        if (d?.reason === 'over_visit_max') {
            return t('loyalty.cc.overVisitMax',
                'That visit would give {{points}} points — more than the {{max}} allowed for one visit',
                { points: d.points, max: d.max });
        }
        return typeof d === 'string' ? t(`loyalty.cc.${d}`, d)
            : t('common.error', 'Something went wrong');
    };

    const run = async (what) => {
        setBusy(true); setError(''); setDone('');
        try {
            if (what === 'points' || what === 'remove') {
                const value = Math.abs(Number(amount) || 0);
                const r = await api.post(`/loyalty/members/${memberId}/points`,
                    { points: what === 'remove' ? -value : value, note,
                      reverses_id: reverses || '' });
                setDone(t('loyalty.cc.pointsChanged', 'Balance is now {{balance}} points',
                    { balance: r.data?.balance ?? 0 }));
            } else if (what === 'cards') {
                const r = await api.post(`/loyalty/members/${memberId}/gift-cards`,
                    { value: Number(amount), count: Number(count) });
                setDone(t('loyalty.cc.cardsAdded', '{{count}} gift cards added',
                    { count: r.data?.added || 0 }));
            } else if (what === 'visit') {
                const r = await api.post(`/loyalty/members/${memberId}/visit`,
                    { amount: Number(amount) || null });
                setDone(t('loyalty.cc.visitAdded', '{{points}} points added',
                    { points: r.data?.points_added || 0 }));
            } else if (what === 'pin') {
                const r = await api.post(`/loyalty/members/${memberId}/pin-reset`);
                setDone(t('loyalty.cc.resetSentTo', 'Reset email sent to {{email}}',
                    { email: r.data?.masked_email || '' }));
            } else if (what === 'delete') {
                await api.delete(`/loyalty/members/${memberId}`);
                onChanged?.();
                onClose?.();
                return;
            }
            setForm(''); setAmount(''); setNote(''); setReverses('');
            await load();
            onChanged?.();
        } catch (e) {
            setError(fail(e));
        } finally { setBusy(false); }
    };

    const removeCard = async (card) => {
        setBusy(true); setError('');
        try {
            await api.delete(`/loyalty/members/${memberId}/gift-cards/${card.id}`);
            await load();
            onChanged?.();
        } catch (e) { setError(fail(e)); } finally { setBusy(false); }
    };

    const member = detail?.member || {};

    // Opening "Edit name" always starts from the name on file, so a half-typed
    // edit that was cancelled can never come back on the next tap.
    useEffect(() => {
        if (form === 'name') setName(member.first_name || '');
    }, [form, member.first_name]);

    const Action = ({ id, label, testid, tone }) => (
        <button type="button" data-testid={testid}
                onClick={() => { setForm(form === id ? '' : id); setDone(''); setError(''); }}
                className={`inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl
                            border-2 px-3 text-sm font-semibold transition-colors
                            ${tone ? ACTION_STYLE[tone] : 'border-rose-400 bg-rose-50 text-rose-700 '
                              + 'hover:bg-rose-100 dark:bg-rose-500/15 dark:border-rose-400/70 '
                              + 'dark:text-rose-200 dark:hover:bg-rose-500/25'}
                            ${form === id ? 'ring-2 ring-offset-1 ring-current dark:ring-offset-slate-900' : ''}`}>
            {tone ? <ActionIcon action={tone} /> : null}
            <span>{label}</span>
        </button>
    );

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 p-3 sm:p-6"
             data-testid="loyalty-member-screen">
            <div className="mx-auto max-w-3xl rounded-2xl bg-white p-5 shadow-xl
                            dark:bg-slate-900">
                <div className="mb-4 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        {/* THE NAME. Read-only until "Edit name" is tapped, so the
                            button is plainly the way in and nothing competes
                            with it. Editing swaps in a field with Save and
                            Cancel; Cancel puts the old name straight back. */}
                        {form === 'name' ? (
                            <div className="flex flex-wrap items-center gap-2">
                                <input value={name} onChange={(e) => setName(e.target.value)}
                                       data-testid="loyalty-member-name-input"
                                       aria-label={t('loyalty.member.editName', 'Edit name')}
                                       autoFocus
                                       className="min-w-0 flex-1 rounded-lg border-2 border-sky-400 bg-transparent
                                                  px-2 py-1 text-lg font-bold outline-none
                                                  dark:border-sky-400/70" />
                                <Button size="sm" disabled={busy || !name.trim()}
                                        data-testid="loyalty-member-name-save"
                                        onClick={async () => {
                                            setBusy(true);
                                            setError('');
                                            try {
                                                await api.put(`/loyalty/members/${memberId}/name`,
                                                              { first_name: name.trim() });
                                                setDone(t('loyalty.member.nameSaved', 'Name saved'));
                                                setForm('');
                                                await load();
                                                if (onChanged) onChanged();
                                            } catch (e) {
                                                setError(fail(e));
                                            }
                                            setBusy(false);
                                        }}>
                                    {t('loyalty.member.saveName', 'Save')}
                                </Button>
                                <Button size="sm" variant="ghost" disabled={busy}
                                        data-testid="loyalty-member-name-cancel"
                                        onClick={() => {
                                            setName(member.first_name || '');
                                            setForm('');
                                            setError('');
                                        }}>
                                    {t('loyalty.member.cancelName', 'Cancel')}
                                </Button>
                            </div>
                        ) : (
                            <h3 className="text-lg font-bold" data-testid="loyalty-member-name">
                                {member.first_name || '—'}
                            </h3>
                        )}
                        <p className="mt-1 text-xs text-slate-500" data-testid="loyalty-member-email">
                            {member.email}
                        </p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={onClose}
                            data-testid="loyalty-member-close">
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                {!detail && <Loader2 className="mx-auto h-5 w-5 animate-spin" />}

                {detail && (
                    <>
                        {/* CONSENT — read only. Nothing here can be edited or
                            removed, by anyone. */}
                        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3
                                        dark:border-emerald-900 dark:bg-emerald-950/30"
                             data-testid="loyalty-member-consent">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-sm font-semibold"
                                   data-testid="loyalty-member-consent-title">
                                    {t('loyalty.consent.title', 'Consent')}
                                </p>
                                <Button size="sm" variant="outline" disabled={busy}
                                        data-testid="loyalty-member-consent-csv"
                                        onClick={async () => {
                                            setBusy(true);
                                            try {
                                                await downloadConsentCsv(
                                                    detail.member?.organization_id, memberId);
                                            } finally { setBusy(false); }
                                        }}>
                                    {t('loyalty.consent.downloadOne', 'Download this consent (CSV)')}
                                </Button>
                            </div>
                            <p className="mt-1 text-xs" data-testid="loyalty-member-consent-given">
                                {detail.consent?.given
                                    ? t('loyalty.consent.given', 'Consent given')
                                    : t('loyalty.consent.none', 'No consent on record')}
                                {detail.consent?.at ? ` · ${when(detail.consent.at)}` : ''}
                                {detail.consent?.language
                                    ? ` · ${detail.consent.language.toUpperCase()}` : ''}
                            </p>
                            {detail.consent?.wording && (
                                <p className="mt-2 whitespace-pre-line rounded-lg bg-white/70 p-2 text-xs
                                              dark:bg-slate-900/60"
                                   data-testid="loyalty-member-consent-wording">
                                    {detail.consent.wording}
                                </p>
                            )}
                            {detail.consent?.note && (
                                <p className="mt-1 text-[11px] text-slate-600 dark:text-slate-400"
                                   data-testid="loyalty-member-consent-note">
                                    {detail.consent.note}
                                </p>
                            )}
                            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400"
                               data-testid="loyalty-member-consent-readonly">
                                {t('loyalty.consent.readOnly',
                                    'Kept permanently. It cannot be edited or deleted.')}
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                            <div className="rounded-xl border border-slate-200 p-3 text-center
                                            dark:border-slate-700">
                                <p className="text-2xl font-bold" data-testid="loyalty-member-points">
                                    {(detail.points || 0).toLocaleString()}
                                </p>
                                <p className="text-xs text-slate-500">{t('loyalty.cc.points', 'Points')}</p>
                            </div>
                            <div className="rounded-xl border border-slate-200 p-3 text-center
                                            dark:border-slate-700">
                                <p className="text-2xl font-bold" data-testid="loyalty-member-bank">
                                    {(detail.bank || []).length}
                                </p>
                                <p className="text-xs text-slate-500">
                                    {t('loyalty.cc.cardsInBank', 'Cards in bank')}
                                </p>
                            </div>
                            <div className="rounded-xl border border-slate-200 p-3 text-center
                                            dark:border-slate-700">
                                <p className="text-2xl font-bold" data-testid="loyalty-member-history-count">
                                    {detail.history_count || 0}
                                </p>
                                <p className="text-xs text-slate-500">
                                    {t('loyalty.member.historyEntries', 'History entries')}
                                </p>
                            </div>
                        </div>

                        <div className="mt-4 flex gap-2">
                            <Button size="sm" variant={tab === 'actions' ? 'default' : 'ghost'}
                                    data-testid="loyalty-member-tab-actions"
                                    onClick={() => setTab('actions')}>
                                {t('loyalty.member.actions', 'Actions')}
                            </Button>
                            <Button size="sm" variant={tab === 'history' ? 'default' : 'ghost'}
                                    data-testid="loyalty-member-tab-history"
                                    onClick={() => setTab('history')}>
                                {t('loyalty.member.history', 'History')}
                            </Button>
                        </div>

                        {tab === 'actions' && (
                            <div className="mt-3 space-y-3" data-testid="loyalty-member-actions">
                                <div className="grid gap-2 sm:grid-cols-3">
                                    <Action id="name" testid="loyalty-member-edit-name" tone="name"
                                            label={t('loyalty.member.editName', 'Edit name')} />
                                    <Action id="points" testid="loyalty-member-add-points" tone="points"
                                            label={t('loyalty.member.addPoints', 'Add points')} />
                                    <Action id="remove" testid="loyalty-member-remove-points" tone="points"
                                            label={t('loyalty.member.removePoints', 'Remove points')} />
                                    <Action id="cards" testid="loyalty-member-add-cards" tone="cards"
                                            label={t('loyalty.cc.addCards', 'Add gift cards')} />
                                    <Action id="visit" testid="loyalty-member-visit" tone="visit"
                                            label={t('loyalty.cc.recordVisit', 'Record a visit')} />
                                    <Action id="pin" testid="loyalty-member-pin-reset" tone="pin"
                                            label={t('loyalty.cc.sendPinReset', 'Send PIN reset email')} />
                                    <Action id="delete" testid="loyalty-member-delete"
                                            label={t('loyalty.member.delete', 'Remove this member')} />
                                </div>

                                {(form === 'points' || form === 'remove') && (
                                    <div className="flex flex-wrap items-center gap-2 rounded-xl border
                                                    border-slate-200 p-3 dark:border-slate-700"
                                         data-testid="loyalty-member-points-form">
                                        <input value={amount} onChange={(e) => setAmount(e.target.value)}
                                               data-testid="loyalty-member-points-amount"
                                               placeholder={t('loyalty.cc.points', 'Points')}
                                               className="w-28 rounded-lg border border-slate-300 px-2 py-1.5 text-sm
                                                          dark:border-slate-600 dark:bg-slate-900" />
                                        <input value={note} onChange={(e) => setNote(e.target.value)}
                                               data-testid="loyalty-member-points-note"
                                               placeholder={t('loyalty.cc.shortNote', 'Short note')}
                                               className="w-48 rounded-lg border border-slate-300 px-2 py-1.5 text-sm
                                                          dark:border-slate-600 dark:bg-slate-900" />
                                        {reverses && (
                                            <span className="text-[11px] text-amber-600"
                                                  data-testid="loyalty-member-correcting">
                                                {t('loyalty.member.correcting', 'Correction of an earlier entry')}
                                            </span>
                                        )}
                                        <Button size="sm" disabled={busy}
                                                data-testid="loyalty-member-points-go"
                                                onClick={() => run(form)}>
                                            {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                                                : t('common.confirm', 'Confirm')}
                                        </Button>
                                    </div>
                                )}

                                {form === 'cards' && (
                                    <div className="flex flex-wrap items-center gap-2 rounded-xl border
                                                    border-slate-200 p-3 dark:border-slate-700"
                                         data-testid="loyalty-member-cards-form">
                                        <input value={amount} onChange={(e) => setAmount(e.target.value)}
                                               data-testid="loyalty-member-card-value"
                                               placeholder={t('loyalty.member.cardValue', 'Value of one card')}
                                               className="w-36 rounded-lg border border-slate-300 px-2 py-1.5 text-sm
                                                          dark:border-slate-600 dark:bg-slate-900" />
                                        <input value={count} onChange={(e) => setCount(e.target.value)}
                                               data-testid="loyalty-member-card-count"
                                               placeholder={t('loyalty.member.howMany', 'How many')}
                                               className="w-28 rounded-lg border border-slate-300 px-2 py-1.5 text-sm
                                                          dark:border-slate-600 dark:bg-slate-900" />
                                        <Button size="sm" disabled={busy}
                                                data-testid="loyalty-member-cards-go"
                                                onClick={() => run('cards')}>
                                            {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                                                : t('common.add', 'Add')}
                                        </Button>
                                    </div>
                                )}

                                {form === 'visit' && (
                                    <div className="flex flex-wrap items-center gap-2 rounded-xl border
                                                    border-slate-200 p-3 dark:border-slate-700"
                                         data-testid="loyalty-member-visit-form">
                                        <input value={amount} onChange={(e) => setAmount(e.target.value)}
                                               data-testid="loyalty-member-visit-amount"
                                               placeholder={t('loyalty.cc.amountSpent', 'Amount spent')}
                                               className="w-36 rounded-lg border border-slate-300 px-2 py-1.5 text-sm
                                                          dark:border-slate-600 dark:bg-slate-900" />
                                        <Button size="sm" disabled={busy}
                                                data-testid="loyalty-member-visit-go"
                                                onClick={() => run('visit')}>
                                            {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                                                : t('common.confirm', 'Confirm')}
                                        </Button>
                                    </div>
                                )}

                                {form === 'pin' && (
                                    <div className="flex items-center gap-2 rounded-xl border border-slate-200
                                                    p-3 dark:border-slate-700"
                                         data-testid="loyalty-member-pin-form">
                                        <p className="text-xs text-slate-500">
                                            {t('loyalty.member.pinLine',
                                                'The member gets a single-use link to choose a new PIN. You never see it.')}
                                        </p>
                                        <Button size="sm" disabled={busy}
                                                data-testid="loyalty-member-pin-go"
                                                onClick={() => run('pin')}>
                                            {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                                                : t('common.confirm', 'Confirm')}
                                        </Button>
                                    </div>
                                )}

                                {form === 'delete' && (
                                    <div className="flex flex-wrap items-center gap-2 rounded-xl border
                                                    border-rose-200 p-3 dark:border-rose-800"
                                         data-testid="loyalty-member-delete-form">
                                        <p className="text-xs text-rose-600">
                                            {t('loyalty.member.deleteLine',
                                                'The member is hidden from this list. Nothing is erased and a super admin can restore it from Deleted items.')}
                                        </p>
                                        <Button size="sm" disabled={busy}
                                                data-testid="loyalty-member-delete-go"
                                                onClick={() => run('delete')}>
                                            {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                                                : t('common.confirm', 'Confirm')}
                                        </Button>
                                    </div>
                                )}

                                <div>
                                    <p className="mb-1 text-xs font-semibold">
                                        {t('loyalty.member.bankCards', 'Gift cards in the bank')}
                                    </p>
                                    {(detail.bank || []).length === 0 ? (
                                        <p className="text-xs text-slate-500" data-testid="loyalty-member-bank-empty">
                                            {t('loyalty.member.noCards', 'No gift cards yet.')}
                                        </p>
                                    ) : (
                                        <ul className="space-y-1" data-testid="loyalty-member-bank-list">
                                            {(detail.bank || []).map((c) => (
                                                <li key={c.id} className="flex items-center justify-between gap-2
                                                                          rounded-lg border border-slate-200 px-3 py-2
                                                                          text-sm dark:border-slate-700">
                                                    <span>{c.code} · {money(c.value)}
                                                        {c.given_at ? ` · ${t('loyalty.member.given', 'given')}` : ''}</span>
                                                    <Button size="sm" variant="outline" disabled={busy}
                                                            data-testid={`loyalty-member-card-delete-${c.id}`}
                                                            onClick={() => removeCard(c)}>
                                                        {t('loyalty.member.removeCard', 'Remove')}
                                                    </Button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        )}

                        {tab === 'history' && (
                            <div className="mt-3 space-y-2" data-testid="loyalty-member-history">
                                <div className="flex items-center gap-2">
                                    <select value={filter} onChange={(e) => setFilter(e.target.value)}
                                            data-testid="loyalty-member-history-filter"
                                            className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm
                                                       dark:border-slate-700 dark:bg-slate-900">
                                        <option value="">{t('loyalty.member.allActions', 'All actions')}</option>
                                        {Object.keys(ACTION_LABELS).map((a) => (
                                            <option key={a} value={a}>
                                                {t(`loyalty.member.action.${a}`, ACTION_LABELS[a])}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-[11px] text-slate-500"
                                       data-testid="loyalty-member-history-locked">
                                        {t('loyalty.member.locked',
                                            'History cannot be edited or deleted. A mistake is put right with a correction.')}
                                    </p>
                                </div>
                                {entries.length === 0 ? (
                                    <p className="text-sm text-slate-500" data-testid="loyalty-member-history-empty">
                                        {t('loyalty.member.historyEmpty', 'Nothing recorded yet.')}
                                    </p>
                                ) : (
                                    <ul className="space-y-1">
                                        {entries.map((e) => (
                                            <li key={e.id} data-testid={`loyalty-history-row-${e.id}`}
                                                className="rounded-lg border border-slate-200 px-3 py-2 text-sm
                                                           dark:border-slate-700">
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <span className="font-semibold">
                                                        {t(`loyalty.member.action.${e.action}`,
                                                            ACTION_LABELS[e.action] || e.action)}
                                                    </span>
                                                    <span className="text-xs text-slate-500">{when(e.created_at)}</span>
                                                </div>
                                                <div className="text-xs text-slate-500">
                                                    {e.actor_kind === 'member'
                                                        ? t('loyalty.member.byMember', 'member')
                                                        : `${e.actor_name}${e.actor_role ? ` · ${e.actor_role}` : ''}`}
                                                    {' — '}<Change entry={e} />
                                                    {e.note ? ` — ${e.note}` : ''}
                                                </div>
                                                {e.reverses_id && (
                                                    <p className="text-[11px] text-amber-600">
                                                        {t('loyalty.member.isCorrection', 'Correction of entry {{id}}',
                                                            { id: e.reverses_id.slice(0, 8) })}
                                                    </p>
                                                )}
                                                {['points_added', 'points_removed', 'visit_points'].includes(e.action) && (
                                                    <button type="button" className="mt-1 text-[11px] underline"
                                                            data-testid={`loyalty-history-correct-${e.id}`}
                                                            onClick={() => {
                                                                const delta = (e.after?.points ?? 0) - (e.before?.points ?? 0);
                                                                setTab('actions');
                                                                setForm(delta > 0 ? 'remove' : 'points');
                                                                setAmount(String(Math.abs(delta)));
                                                                setReverses(e.id);
                                                                setNote(t('loyalty.member.correctionNote', 'Correction'));
                                                            }}>
                                                        {t('loyalty.member.correct', 'Add a correction')}
                                                    </button>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        {done && <p className="mt-3 text-xs text-emerald-600"
                                    data-testid="loyalty-member-done">{done}</p>}
                        {error && <p className="mt-3 text-xs text-rose-500"
                                     data-testid="loyalty-member-error">{error}</p>}
                    </>
                )}
            </div>
        </div>
    );
};

export default MemberScreen;
